package ai.jessie.web.projects

import ai.jessie.web.onboarding.HTTP_CONFLICT
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.problem

// UC-010 outcomes. Everything else keeps the UC-006 tenant-denial presentation unchanged.
internal fun ProjectRequestFailure.projectProblem(): OnboardingProblem =
    when {
        // A12, A13: no record detail, only the neutral absence.
        status == HTTP_NOT_FOUND -> unavailableProjectProblem()
        detail.code == "STALE_PROJECT" ->
            OnboardingProblem(
                "Das Projekt wurde inzwischen geändert. Es wurde nichts gespeichert. Bitte lade " +
                    "den aktuellen Stand und trage gewünschte Änderungen erneut ein."
            )
        // A3, BR-055.
        detail.code == "CUSTOMER_CHANGE_LOCKED" ->
            OnboardingProblem(
                "Der Kunde kann nicht geändert werden, weil Arbeit dieses Projekts verrechnet " +
                    "ist. Es wurde nichts gespeichert. Erfasse für den anderen Kunden ein neues " +
                    "Projekt.",
                listOf(CUSTOMER_ID),
            )
        // B1: unavailable proof of absence is not a statement that no invoice exists.
        detail.code == "CUSTOMER_CHANGE_UNAVAILABLE" ->
            OnboardingProblem(
                "Der Kunde kann zurzeit nicht geändert werden, weil Rechnungsverknüpfungen nicht " +
                    "geprüft werden können. Es wurde nichts gespeichert; der bisherige Kunde " +
                    "bleibt erhalten.",
                listOf(CUSTOMER_ID),
            )
        detail.code == "CONFIRMATION_REQUIRED" ->
            OnboardingProblem("Die Bestätigung fehlt. Es wurde nichts gespeichert.")
        status == HTTP_CONFLICT ->
            OnboardingProblem(
                "Das Projekt kann nicht geändert werden. Es wurde nichts gespeichert."
            )
        // A11: nothing may be presented as saved.
        status >= HTTP_SERVER_ERROR ->
            OnboardingProblem(
                "Das Projekt konnte nicht gespeichert werden. Es wurde nichts gespeichert. Bitte " +
                    "bestätige erneut oder brich ab."
            )
        else -> OnboardingRequestFailure(status, detail.code).problem()
    }

// A8: the record is preserved; `deactivateId` is the offered alternative, when it is available.
internal fun referencedProblem(deactivateId: String?) =
    OnboardingProblem(
        "Der Eintrag wurde bereits verwendet und kann nicht entfernt werden. Es wurde nichts " +
            "entfernt. Deaktiviere ihn stattdessen; bestehende Verknüpfungen bleiben erhalten.",
        listOfNotNull(deactivateId),
    )

internal fun unavailableProjectProblem() =
    OnboardingProblem("Nicht verfügbar oder du hast keinen Zugriff.")

internal fun projectValidationProblem(problems: Map<String, String>) =
    OnboardingProblem(
        "Bitte korrigiere die markierten Felder. Es wurde nichts gespeichert.",
        problems.keys.map { "project-$it" },
    )

internal fun uncertainProjectProblem() =
    OnboardingProblem(
        "Die Antwort konnte nicht bestätigt werden. Bitte brich ab und lade neu, um den " +
            "gespeicherten Stand zu prüfen."
    )

internal fun deactivateId(positionId: String?) =
    if (positionId == null) "project-deactivate" else "position-deactivate-$positionId"

internal const val CUSTOMER_ID = "project-customerId"
internal const val HTTP_SERVER_ERROR = 500
private const val HTTP_NOT_FOUND = 404
